import { useCallback, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import type { DocumentSection } from "@/api/schemas";
import { useDocgenStore } from "./useDocgenStore";

/**
 * Sprint B — W6.
 *
 * Wires the j/k/o/r/e/? cheat-sheet shortcuts on the docgen view.
 * Pure side-effects: listens at the document level (no input focus
 * needed) and pokes ``useDocgenStore`` so individual ``DocSectionCard``
 * instances react to the action queue.
 *
 * Caller is responsible for the ``?`` cheat sheet visibility — we
 * surface a callback rather than rendering the dialog in here so the
 * shortcuts module stays headless.
 */

interface UseDocgenShortcutsArgs {
  sections: DocumentSection[];
  onJump: (sectionId: string) => void;
  onShowCheatSheet: () => void;
  enabled?: boolean;
}

export function useDocgenShortcuts({
  sections,
  onJump,
  onShowCheatSheet,
  enabled = true,
}: UseDocgenShortcutsArgs) {
  const focusedId = useDocgenStore((s) => s.focusedSectionId);
  const setFocused = useDocgenStore((s) => s.setFocusedSection);
  const requestAction = useDocgenStore((s) => s.requestAction);

  const moveFocus = useCallback(
    (delta: 1 | -1) => {
      if (sections.length === 0) return;
      const idx = focusedId
        ? sections.findIndex((s) => s.section_id === focusedId)
        : -1;
      // First press picks the first/last section depending on direction.
      const nextIdx = idx === -1
        ? delta === 1 ? 0 : sections.length - 1
        : Math.min(Math.max(idx + delta, 0), sections.length - 1);
      const next = sections[nextIdx];
      if (!next) return;
      setFocused(next.section_id);
      onJump(next.section_id);
    },
    [sections, focusedId, setFocused, onJump],
  );

  useHotkeys("j", () => moveFocus(1), { enabled });
  useHotkeys("k", () => moveFocus(-1), { enabled });

  useHotkeys(
    "o",
    () => {
      if (!focusedId) return;
      requestAction({ sectionId: focusedId, type: "toggle" });
    },
    { enabled },
    [focusedId, requestAction],
  );

  useHotkeys(
    "r",
    () => {
      if (!focusedId) return;
      requestAction({ sectionId: focusedId, type: "regenerate" });
    },
    { enabled },
    [focusedId, requestAction],
  );

  useHotkeys(
    "e",
    () => {
      if (!focusedId) return;
      requestAction({ sectionId: focusedId, type: "refine" });
    },
    { enabled },
    [focusedId, requestAction],
  );

  useHotkeys("shift+/", () => onShowCheatSheet(), { enabled });

  // Reset focus on unmount so the next docgen run starts clean.
  useEffect(() => {
    return () => setFocused(null);
  }, [setFocused]);
}

export const SHORTCUT_HELP: ReadonlyArray<{
  key: string;
  desc: string;
}> = [
  { key: "j", desc: "Sljedeća sekcija" },
  { key: "k", desc: "Prethodna sekcija" },
  { key: "o", desc: "Otvori / zatvori sekciju" },
  { key: "r", desc: "Regeneriraj sekciju (s pregledom razlika)" },
  { key: "e", desc: "AI dorada" },
  { key: "?", desc: "Prikaži ovu pomoć" },
];
